import { useState, useMemo } from "react";
import { Filter, X } from "lucide-react";
import { Column } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface FilterOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  color?: string;
}

interface ColumnMultiSelectFilterProps<TData> {
  column: Column<TData, unknown>;
  title: string;
  options: FilterOption[];
  emptyMessage?: string;
}

export function ColumnMultiSelectFilter<TData>({
  column,
  title,
  options,
  emptyMessage = "No options available"
}: ColumnMultiSelectFilterProps<TData>) {
  const [searchQuery, setSearchQuery] = useState("");
  const filterValue = column.getFilterValue() as string[] | undefined;
  const selectedValues = filterValue || [];
  const isFiltered = selectedValues.length > 0;
  
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;
    return options.filter(opt => 
      opt.label.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [options, searchQuery]);
  
  const handleToggle = (value: string, checked: boolean) => {
    const newValues = checked
      ? [...selectedValues, value]
      : selectedValues.filter(v => v !== value);
    
    column.setFilterValue(newValues.length > 0 ? newValues : undefined);
  };
  
  const clearFilter = () => {
    column.setFilterValue(undefined);
    setSearchQuery("");
  };
  
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className={cn(
            "h-8 w-8",
            isFiltered && "text-primary"
          )}
        >
          <Filter className={cn(
            "h-4 w-4",
            isFiltered && "fill-primary"
          )} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="start">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-base font-semibold">{title}</Label>
            {isFiltered && (
              <Button variant="ghost" size="sm" onClick={clearFilter}>
                <X className="mr-1 h-3 w-3" />
                Clear
              </Button>
            )}
          </div>
          
          {options.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              {emptyMessage}
            </p>
          ) : (
            <>
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9"
              />
              
              <ScrollArea className="h-64">
                <div className="space-y-2 pr-4">
                  {filteredOptions.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">
                      No matches found
                    </p>
                  ) : (
                    filteredOptions.map(option => (
                      <div key={option.value} className="flex items-center gap-2">
                        <Checkbox
                          id={`filter-${option.value}`}
                          checked={selectedValues.includes(option.value)}
                          onCheckedChange={(checked) => handleToggle(option.value, !!checked)}
                        />
                        <Label
                          htmlFor={`filter-${option.value}`}
                          className="flex items-center gap-2 flex-1 cursor-pointer font-normal"
                        >
                          {option.color && (
                            <div
                              className="w-3 h-3 rounded-full flex-shrink-0"
                              style={{ backgroundColor: option.color }}
                            />
                          )}
                          {option.icon && (
                            <div className="flex-shrink-0">
                              {option.icon}
                            </div>
                          )}
                          <span className="flex-1">{option.label}</span>
                        </Label>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
              
              <div className="text-xs text-muted-foreground pt-2 border-t">
                {selectedValues.length === 0
                  ? "Select options to filter"
                  : `${selectedValues.length} option${selectedValues.length > 1 ? 's' : ''} selected`
                }
              </div>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
